import { Link } from "react-router-dom";
import { 
  Bath, 
  Grid3X3, 
  Calculator, 
  ArrowRight, 
  Home,
  Sparkles,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const EstimateMenuPage = () => {
  const estimatorTools = [
    {
      id: "bathroom",
      icon: Bath,
      title: "Bathroom Renovation Planner",
      description: "Complete bathroom refit estimator with visual layout planner, fixture selection, and detailed scope matrix.",
      features: [
        "Visual bathroom layout selector",
        "Drag-and-drop fixture planning",
        "22-section scope matrix",
        "Tile quantity calculator"
      ],
      link: "/bathroom-refit-quote",
      color: "bg-blue-500",
      popular: true
    },
    {
      id: "floor",
      icon: Grid3X3,
      title: "Floor Tiling Estimator",
      description: "Get an instant estimate for floor tiling projects including hallways, kitchens, living areas and more.",
      features: [
        "Multiple tile types",
        "Pattern layout options",
        "Subfloor preparation",
        "Underfloor heating"
      ],
      link: "/floor-tiling-quote",
      color: "bg-emerald-500",
      popular: false
    },
    {
      id: "general",
      icon: Home,
      title: "General Tiling Quote",
      description: "Quick estimate for kitchen splashbacks, wall tiling, feature walls and other tiling projects.",
      features: [
        "Kitchen tiling",
        "Splashbacks",
        "Feature walls",
        "Wall tiling"
      ],
      link: "/quote",
      color: "bg-amber-500",
      popular: false
    },
    {
      id: "tile-calc",
      icon: Calculator,
      title: "Tile Quantity Calculator",
      description: "Calculate how many tiles and boxes you need for your project based on area and tile size.",
      features: [
        "Multiple tile sizes",
        "Waste allowance",
        "Box calculation",
        "Material cost estimate"
      ],
      link: "/tile-calculator",
      color: "bg-purple-500",
      popular: false
    }
  ];

  return (
    <div data-testid="estimate-menu-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Calculator className="w-4 h-4" />
            <span className="text-sm font-medium">Free Instant Estimates</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
            Choose Your Estimator Tool
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Select the right tool for your project and get an instant estimate. 
            All estimates are free with no obligation.
          </p>
        </div>
      </section>

      {/* Estimator Tools Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {estimatorTools.map((tool) => (
              <Link 
                key={tool.id} 
                to={tool.link}
                className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
                data-testid={`estimator-${tool.id}`}
              >
                {tool.popular && (
                  <div className="absolute top-4 right-4 bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                
                <div className="p-8">
                  <div className={`w-14 h-14 ${tool.color} rounded-xl flex items-center justify-center mb-6`}>
                    <tool.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                    {tool.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-6">
                    {tool.description}
                  </p>
                  
                  <ul className="space-y-2 mb-6">
                    {tool.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center text-primary font-medium group-hover:gap-3 transition-all">
                    Start Estimate
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Not sure which tool to use?</h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            For complete bathroom renovations, use our <strong>Bathroom Renovation Planner</strong>. 
            For floor-only projects, try the <strong>Floor Tiling Estimator</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+447547393455">
              <Button variant="outline" className="border-2">
                Call 07547 393 455
              </Button>
            </a>
            <Link to="/contact">
              <Button className="bg-primary hover:bg-primary/90">
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EstimateMenuPage;
